// src/pages/AddResponse.js
import React, { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, query, orderBy } from "firebase/firestore";
import { db } from "../firebase"; // ADATTA AL TUO PATH FIREBASE
import LoadingSpinner from "../components/ui/LoadingSpinner";

/**
 * Dashboard risultati sondaggio:
 * - Sezioni come il questionario
 * - Grafici per risposte chiuse (progress bar)
 * - Accordion/collapse per le risposte aperte
 */

function safeDate(ts) {
  try {
    if (!ts) return null;
    if (typeof ts.toDate === "function") return ts.toDate();
    if (ts instanceof Date) return ts;
    if (typeof ts === "number" || typeof ts === "string") {
      const d = new Date(ts);
      return isNaN(d) ? null : d;
    }
    return null;
  } catch {
    return null;
  }
}

function normalizeYesNo(v) {
  if (v === true) return "Sì";
  if (v === false) return "No";
  if (typeof v !== "string") return "";
  const s = v.trim().toLowerCase();
  if (!s) return "";
  if (s === "si" || s === "sì" || s === "yes") return "Sì";
  if (s === "no") return "No";
  return v;
}

function normalizeArray(v) {
  if (Array.isArray(v)) return v.filter(Boolean);
  return [];
}

function normalizeString(v) {
  if (typeof v === "string") return v.trim();
  return v == null ? "" : String(v);
}

function normalizeNumber(v, fallback = null) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

function countBy(values) {
  const m = new Map();
  for (const v of values) {
    const key = v == null || v === "" ? "(Vuoto)" : String(v);
    m.set(key, (m.get(key) || 0) + 1);
  }
  return Array.from(m.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count);
}

function rangeCount(values, min = 1, max = 10) {
  const buckets = Array.from({ length: max - min + 1 }, (_, i) => ({
    label: String(min + i),
    count: 0
  }));
  for (const v of values) {
    const n = normalizeNumber(v, null);
    if (n == null) continue;
    const idx = clamp(Math.round(n), min, max) - min;
    buckets[idx].count += 1;
  }
  return buckets;
}

function sumCounts(items) {
  return items.reduce((acc, x) => acc + (x.count || 0), 0);
}

// Normalizzazione “soft” per regione/provincia (evita duplicati tipo "lazio" / "Lazio ")
function normalizePlace(s) {
  const t = normalizeString(s);
  if (!t) return "";
  const cleaned = t.replace(/\s+/g, " ").trim();
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

function SectionHeader({ title, subtitle }) {
  return (
    <div className="d-flex align-items-start justify-content-between gap-3 mt-4 mb-2">
      <div>
        <h5 className="mb-1">{title}</h5>
        {subtitle ? <div className="text-muted small">{subtitle}</div> : null}
      </div>
    </div>
  );
}

function BarChart({ title, data, note }) {
  const total = sumCounts(data);
  const max = Math.max(1, ...data.map((d) => d.count));

  return (
    <div className="card border-0 shadow-sm h-100">
      <div className="card-body">
        <div className="d-flex align-items-start justify-content-between gap-3">
          <div>
            <h6 className="mb-1">{title}</h6>
            {note ? <div className="text-muted small">{note}</div> : null}
          </div>
          <span className="badge text-bg-dark">{total}</span>
        </div>

        <div className="mt-3 d-flex flex-column gap-2">
          {data.length === 0 ? (
            <div className="text-muted small">Nessun dato.</div>
          ) : (
            data.map((d) => {
              const pct = Math.round((d.count / max) * 100);
              const pctOfTotal = total ? Math.round((d.count / total) * 100) : 0;
              return (
                <div key={d.label} className="d-flex align-items-center gap-2">
                  <div  className="small text-muted text-truncate">
                    {d.label}
                  </div>
                  <div className="flex-grow-1">
                    <div className="progress" style={{ height: 10 }}>
                      <div className="progress-bar" role="progressbar" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                  <div style={{ width: 70 }} className="small text-end">
                    <span className="fw-semibold">{d.count}</span>{" "}
                    <span className="text-muted">({pctOfTotal}%)</span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

function RangeChart({ title, values, min = 1, max = 10 }) {
  const data = rangeCount(values, min, max);
  const total = sumCounts(data);
  const avg =
    total > 0
      ? (data.reduce((acc, x) => acc + Number(x.label) * x.count, 0) / total).toFixed(1)
      : "-";

  return <BarChart title={title} data={data} note={`Media: ${avg} su ${max} (risposte: ${total})`} />;
}

function OpenAnswersAccordion({ title, items }) {
  const accId = useMemo(() => `acc-${title.replace(/[^\w]+/g, "-").toLowerCase()}`, [title]);

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body">
        <div className="d-flex align-items-start justify-content-between gap-3 mb-2">
          <h6 className="mb-0">{title}</h6>
          <span className="badge text-bg-dark">{items.length}</span>
        </div>

        {items.length === 0 ? (
          <div className="text-muted small">Nessuna risposta.</div>
        ) : (
          <div className="accordion" id={accId}>
            {items.map((it, idx) => {
              const id = `${accId}-${idx}`;
              return (
                <div className="accordion-item" key={id}>
                  <h2 className="accordion-header">
                    <button
                      className={`accordion-button ${idx === 0 ? "" : "collapsed"}`}
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target={`#collapse-${id}`}
                      aria-expanded={idx === 0 ? "true" : "false"}
                      aria-controls={`collapse-${id}`}
                    >
                      <span className="me-2">#{idx + 1}</span>
                      <span className="text-truncate">{it.meta || "Risposta"}</span>
                    </button>
                  </h2>
                  <div id={`collapse-${id}`} className={`accordion-collapse collapse ${idx === 0 ? "show" : ""}`}>
                    <div className="accordion-body">
                      <div className="small text-muted mb-2">{it.meta}</div>
                      <div style={{ whiteSpace: "pre-wrap" }}>{it.text}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AddResponse() {
  const [loading, setLoading] = useState(true);
  const [responses, setResponses] = useState([]);
  const [search, setSearch] = useState("");
  const [onlyWithOpen, setOnlyWithOpen] = useState(false);

  useEffect(() => {
    const qy = query(collection(db, "pollResponses"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(
      qy,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setResponses(rows);
        setLoading(false);
      },
      (err) => {
        console.error("Errore lettura pollResponses", err);
        setResponses([]);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const normalized = useMemo(() => {
    return responses.map((r) => {
      const a = r.answers || {};

      const row = {
        id: r.id,
        uid: r.uid || "",
        email: r.email || "",
        createdAt: safeDate(r.createdAt),

        // Sez 1
        q1: normalizeNumber(a.q1, null),
        q2: normalizeNumber(a.q2, null),
        q3: normalizeString(a.q3),
        q4: normalizeString(a.q4),

        // Sez 2
        q5: normalizeArray(a.q5),
        q5_other: normalizeString(a.q5_other),
        q6: normalizeString(a.q6),
        q7: normalizeString(a.q7),

        // Sez 3
        q8: normalizeNumber(a.q8, null),
        q9: normalizeNumber(a.q9, null),
        q10: normalizeString(a.q10),
        q11: normalizeString(a.q11),
        q12_roadTo: normalizeYesNo(a.q12_roadTo),
        q15_mode: normalizeString(a.q15_mode ?? a.q11_mode),

        // Sez 4
        q16: normalizeNumber(a.q16, null),
        q13_socialFollow: normalizeYesNo(a.q13_socialFollow),
        q14_merch: normalizeYesNo(a.q14_merch),

        // Sez 5
        age: normalizeString(a.age),
        region: normalizePlace(a.region),
        province: normalizePlace(a.province),
        gender: normalizeString(a.gender),

        // Finale
        feedback: normalizeString(a.feedback)
      };

      if (row.q5.includes("Altre") && row.q5_other) {
        row.q5 = row.q5.map((x) => (x === "Altre" ? `Altre: ${row.q5_other}` : x));
      }

      return row;
    });
  }, [responses]);

  const normalizedMap = useMemo(() => {
    const m = new Map();
    for (const r of normalized) m.set(r.id, r);
    return m;
  }, [normalized]);

  const stats = useMemo(() => {
    const total = normalized.length;

    const q5All = normalized.flatMap((r) => r.q5 || []);
    const q5Counts = countBy(q5All);

    const closed = {
      // Sez 1
      q1: normalized.map((r) => r.q1).filter((x) => x != null),
      q2: normalized.map((r) => r.q2).filter((x) => x != null),
      q3: countBy(normalized.map((r) => r.q3).filter(Boolean)),
      q4: countBy(normalized.map((r) => r.q4).filter(Boolean)),

      // Sez 2
      q5Counts,
      q6: countBy(normalized.map((r) => r.q6).filter(Boolean)),
      q7: countBy(normalized.map((r) => r.q7).filter(Boolean)),

      // Sez 3
      q8: normalized.map((r) => r.q8).filter((x) => x != null),
      q9: normalized.map((r) => r.q9).filter((x) => x != null),
      q12_roadTo: countBy(normalized.map((r) => r.q12_roadTo).filter(Boolean)),
      q15_mode: countBy(normalized.map((r) => r.q15_mode).filter(Boolean)),

      // Sez 4
      q16: normalized.map((r) => r.q16).filter((x) => x != null),
      q13_socialFollow: countBy(normalized.map((r) => r.q13_socialFollow).filter(Boolean)),
      q14_merch: countBy(normalized.map((r) => r.q14_merch).filter(Boolean)),

      // Sez 5 (TUTTE le facoltative)
      age: countBy(normalized.map((r) => r.age).filter(Boolean)),
      gender: countBy(normalized.map((r) => r.gender).filter(Boolean)),
      region: countBy(normalized.map((r) => r.region).filter(Boolean)),
      province: countBy(normalized.map((r) => r.province).filter(Boolean))
    };

    const open = {
      q10: normalized
        .filter((r) => r.q10)
        .map((r) => ({
          text: r.q10,
          meta: `${r.email || r.uid || "utente"} • ${r.createdAt ? r.createdAt.toLocaleString("it-IT") : "data n/d"}`
        })),
      q11: normalized
        .filter((r) => r.q11)
        .map((r) => ({
          text: r.q11,
          meta: `${r.email || r.uid || "utente"} • ${r.createdAt ? r.createdAt.toLocaleString("it-IT") : "data n/d"}`
        })),
      feedback: normalized
        .filter((r) => r.feedback)
        .map((r) => ({
          text: r.feedback,
          meta: `${r.email || r.uid || "utente"} • ${r.createdAt ? r.createdAt.toLocaleString("it-IT") : "data n/d"}`
        }))
    };

    const last = normalized[0]?.createdAt || null;

    return { total, last, closed, open };
  }, [normalized]);

  const openAllMerged = useMemo(() => {
    const merged = normalized.map((r) => {
      const pieces = [
        r.q10 ? `Q10: ${r.q10}` : "",
        r.q11 ? `Q11: ${r.q11}` : "",
        r.feedback ? `Feedback: ${r.feedback}` : ""
      ].filter(Boolean);

      return {
        id: r.id,
        email: r.email || r.uid || "utente",
        createdAt: r.createdAt,
        text: pieces.join("\n\n"),
        hasOpen: pieces.length > 0
      };
    });

    const s = search.trim().toLowerCase();
    return merged.filter((x) => {
      if (onlyWithOpen && !x.hasOpen) return false;
      if (!s) return true;
      return (x.text || "").toLowerCase().includes(s) || (x.email || "").toLowerCase().includes(s);
    });
  }, [normalized, search, onlyWithOpen]);

  if (loading) return <LoadingSpinner fullPage />;

  return (
    <div className="container admin-page py-4">
      {/* HEADER MINIMAL */}
      <div className="mb-4">
        <h3 className="mb-2">Risultati Sondaggio</h3>
        <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
          <div className="d-flex align-items-center gap-2">
            <span className="badge bg-dark rounded-pill">{stats.total}</span>
            <span className="text-muted small">risposte totali</span>
          </div>
          {stats.last && (
            <div className="d-flex align-items-center gap-2">
              <i className="bi bi-clock text-muted"></i>
              <span className="text-muted small">
                ultima: {stats.last.toLocaleDateString("it-IT")}
              </span>
            </div>
          )}
        </div>

        {/* FILTRI MINIMAL */}
        <div className="d-flex flex-column flex-sm-row gap-3 align-items-start align-items-sm-center">
          <div className="input-group input-group-sm" style={{ maxWidth: '300px' }}>
            <span className="input-group-text bg-white border">
              <i className="bi bi-search" />
            </span>
            <input
              className="form-control border"
              placeholder="Cerca..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id="onlyOpenSwitch"
              checked={onlyWithOpen}
              onChange={(e) => setOnlyWithOpen(e.target.checked)}
            />
            <label className="form-check-label small" htmlFor="onlyOpenSwitch">
              Solo risposte aperte
            </label>
          </div>
        </div>
      </div>

      {/* SEZIONE 1 - ESPERIENZA DI GIOCO */}
      <div className="mb-5">
        <h5 className="mb-3 pb-2 border-bottom">Esperienza di gioco</h5>
        <div className="row g-3">
          <div className="col-12 col-lg-6">
            <div className="card border">
              <div className="card-body">
                <h6 className="mb-3">Q1 • Gradimento esperienza</h6>
                {stats.closed.q1.length > 0 && (
                  <>
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div>
                        <div className="h3 mb-0">
                          {(stats.closed.q1.reduce((a, b) => a + b, 0) / stats.closed.q1.length).toFixed(1)}
                        </div>
                        <div className="text-muted small">media su 10</div>
                      </div>
                      <div className="text-end">
                        <div className="small text-muted">risposte</div>
                        <div className="h5 mb-0">{stats.closed.q1.length}</div>
                      </div>
                    </div>
                    <div className="mt-3">
                      {Array.from({ length: 10 }, (_, i) => {
                        const count = stats.closed.q1.filter(v => Math.round(v) === i + 1).length;
                        const percentage = (count / stats.closed.q1.length) * 100;
                        return (
                          <div key={i} className="d-flex align-items-center mb-2">
                            <div className="text-muted small" style={{ width: '30px' }}>{i + 1}</div>
                            <div className="flex-grow-1">
                              <div className="progress" style={{ height: '8px' }}>
                                <div
                                  className="progress-bar bg-primary"
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                            </div>
                            <div className="text-muted small ms-2" style={{ width: '40px' }}>
                              {count} ({percentage.toFixed(0)}%)
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-6">
            <div className="card border">
              <div className="card-body">
                <h6 className="mb-3">Q2 • Consiglieresti</h6>
                {stats.closed.q2.length > 0 && (
                  <>
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div>
                        <div className="h3 mb-0">
                          {(stats.closed.q2.reduce((a, b) => a + b, 0) / stats.closed.q2.length).toFixed(1)}
                        </div>
                        <div className="text-muted small">media su 10</div>
                      </div>
                      <div className="text-end">
                        <div className="small text-muted">risposte</div>
                        <div className="h5 mb-0">{stats.closed.q2.length}</div>
                      </div>
                    </div>
                    <div className="mt-3">
                      {Array.from({ length: 10 }, (_, i) => {
                        const count = stats.closed.q2.filter(v => Math.round(v) === i + 1).length;
                        const percentage = (count / stats.closed.q2.length) * 100;
                        return (
                          <div key={i} className="d-flex align-items-center mb-2">
                            <div className="text-muted small" style={{ width: '30px' }}>{i + 1}</div>
                            <div className="flex-grow-1">
                              <div className="progress" style={{ height: '8px' }}>
                                <div
                                  className="progress-bar bg-success"
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                            </div>
                            <div className="text-muted small ms-2" style={{ width: '40px' }}>
                              {count} ({percentage.toFixed(0)}%)
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-6">
            <div className="card border">
              <div className="card-body">
                <h6 className="mb-3">Q3 • Da quanto tempo giochi</h6>
                <BarChart data={stats.closed.q3} />
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-6">
            <div className="card border">
              <div className="card-body">
                <h6 className="mb-3">Q4 • Frequenza utilizzo app</h6>
                <BarChart data={stats.closed.q4} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SEZIONE 2 - ABITUDINI VISIONE */}
      <div className="mb-5">
        <h5 className="mb-3 pb-2 border-bottom">Abitudini di visione</h5>
        <div className="row g-3">
          <div className="col-12 col-lg-6">
            <div className="card border">
              <div className="card-body">
                <h6 className="mb-3">Q5 • Federazioni seguite</h6>
                {stats.closed.q5Counts.map((item, index) => {
                  const percentage = (item.count / stats.total) * 100;
                  return (
                    <div key={index} className="d-flex align-items-center mb-3">
                      <div className="flex-grow-1 me-3">
                        <div className="small">{item.label}</div>
                        <div className="progress" style={{ height: '6px' }}>
                          <div
                            className="progress-bar bg-info"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                      <div className="text-muted small" style={{ minWidth: '50px' }}>
                        {item.count} ({percentage.toFixed(0)}%)
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-6">
            <div className="card border">
              <div className="card-body">
                <h6 className="mb-3">Q6 • Federazione preferita</h6>
                <BarChart data={stats.closed.q6} />
              </div>
            </div>
          </div>

          <div className="col-12">
            <div className="card border">
              <div className="card-body">
                <h6 className="mb-3">Q7 • Visione show</h6>
                <BarChart data={stats.closed.q7} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SEZIONE 3 - GAMEPLAY */}
      <div className="mb-5">
        <h5 className="mb-3 pb-2 border-bottom">Gameplay e funzionalità</h5>
        <div className="row g-3">
          <div className="col-12 col-lg-6">
            <div className="card border">
              <div className="card-body">
                <h6 className="mb-3">Q8 • Chiarezza regolamento</h6>
                {stats.closed.q8.length > 0 && (
                  <>
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="h3 mb-0">
                        {(stats.closed.q8.reduce((a, b) => a + b, 0) / stats.closed.q8.length).toFixed(1)}
                      </div>
                      <div className="text-muted small">media su 10</div>
                    </div>
                    <RangeChart values={stats.closed.q8} />
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-6">
            <div className="card border">
              <div className="card-body">
                <h6 className="mb-3">Q9 • Equilibrio bonus/malus</h6>
                {stats.closed.q9.length > 0 && (
                  <>
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="h3 mb-0">
                        {(stats.closed.q9.reduce((a, b) => a + b, 0) / stats.closed.q9.length).toFixed(1)}
                      </div>
                      <div className="text-muted small">media su 10</div>
                    </div>
                    <RangeChart values={stats.closed.q9} />
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-6">
            <div className="card border">
              <div className="card-body">
                <h6 className="mb-3">Q12 • Partecipi Road To</h6>
                <BarChart data={stats.closed.q12_roadTo} />
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-6">
            <div className="card border">
              <div className="card-body">
                <h6 className="mb-3">Q15 • Modalità preferita</h6>
                <BarChart data={stats.closed.q15_mode} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SEZIONE 4 - COMMUNITY */}
      <div className="mb-5">
        <h5 className="mb-3 pb-2 border-bottom">Community e coinvolgimento</h5>
        <div className="row g-3">
          <div className="col-12 col-lg-6">
            <div className="card border">
              <div className="card-body">
                <h6 className="mb-3">Q13 • Segui sui social</h6>
                <BarChart data={stats.closed.q13_socialFollow} />
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-6">
            <div className="card border">
              <div className="card-body">
                <h6 className="mb-3">Q14 • Interesse merch</h6>
                <BarChart data={stats.closed.q14_merch} />
              </div>
            </div>
          </div>

          <div className="col-12">
            <div className="card border">
              <div className="card-body">
                <h6 className="mb-3">Q16 • Coinvolgimento competizione</h6>
                {stats.closed.q16.length > 0 && (
                  <>
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="h3 mb-0">
                        {(stats.closed.q16.reduce((a, b) => a + b, 0) / stats.closed.q16.length).toFixed(1)}
                      </div>
                      <div className="text-muted small">media su 10</div>
                    </div>
                    <RangeChart values={stats.closed.q16} />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SEZIONE 5 - DEMOGRAFIA */}
      <div className="mb-5">
        <h5 className="mb-3 pb-2 border-bottom">Informazioni demografiche</h5>
        <div className="row g-3">
          <div className="col-12 col-lg-3">
            <div className="card border">
              <div className="card-body">
                <h6 className="mb-3">Età</h6>
                <BarChart data={stats.closed.age} />
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-3">
            <div className="card border">
              <div className="card-body">
                <h6 className="mb-3">Genere</h6>
                <BarChart data={stats.closed.gender} />
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-3">
            <div className="card border">
              <div className="card-body">
                <h6 className="mb-3">Top Regioni</h6>
                {stats.closed.region.length === 0 ? (
                  <div className="text-muted small">Nessun dato.</div>
                ) : (
                  stats.closed.region.map((item, idx) => {
                    const percentage = stats.total ? (item.count / stats.total) * 100 : 0;
                    return (
                      <div key={idx} className="d-flex align-items-center mb-3">
                        <div className="flex-grow-1 me-3">
                          <div className="small text-truncate">{item.label}</div>
                          <div className="progress" style={{ height: '6px' }}>
                            <div className="progress-bar bg-primary" style={{ width: `${percentage}%` }} />
                          </div>
                        </div>
                        <div className="text-muted small" style={{ minWidth: '50px' }}>
                          {item.count} ({percentage.toFixed(0)}%)
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-3">
            <div className="card border">
              <div className="card-body">
                <h6 className="mb-3">Top Province</h6>
                {stats.closed.province.length === 0 ? (
                  <div className="text-muted small">Nessun dato.</div>
                ) : (
                  stats.closed.province.map((item, idx) => {
                    const percentage = stats.total ? (item.count / stats.total) * 100 : 0;
                    return (
                      <div key={idx} className="d-flex align-items-center mb-3">
                        <div className="flex-grow-1 me-3">
                          <div className="small text-truncate">{item.label}</div>
                          <div className="progress" style={{ height: '6px' }}>
                            <div className="progress-bar bg-secondary" style={{ width: `${percentage}%` }} />
                          </div>
                        </div>
                        <div className="text-muted small" style={{ minWidth: '50px' }}>
                          {item.count} ({percentage.toFixed(0)}%)
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* RISPOSTE APERTE - VERSIONE SIMPLIFICATA */}
      <div className="mb-5">
        <h5 className="mb-3 pb-2 border-bottom">Risposte aperte</h5>
        <div className="row g-3">
          <div className="col-12">
            <div className="card border">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h6 className="mb-0">Q10 • Cosa miglioreresti</h6>
                  <span className="badge bg-secondary">{stats.open.q10.length}</span>
                </div>
                {stats.open.q10.length === 0 ? (
                  <div className="text-muted small text-center py-3">Nessuna risposta</div>
                ) : (
                  <div className="accordion accordion-flush" id="accordionQ10">
                    {stats.open.q10.map((item, index) => (
                      <div className="accordion-item border-0" key={index}>
                        <h2 className="accordion-header">
                          <button
                            className="accordion-button collapsed py-2"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target={`#collapseQ10-${index}`}
                          >
                            <span className="me-2 text-muted">#{index + 1}</span>
                            <span className="text-truncate">{item.meta.split('•')[0]?.trim() || 'Utente'}</span>
                          </button>
                        </h2>
                        <div
                          id={`collapseQ10-${index}`}
                          className="accordion-collapse collapse"
                          data-bs-parent="#accordionQ10"
                        >
                          <div className="accordion-body pt-0">
                            <div className="text-muted small mb-2">{item.meta}</div>
                            <div className="border-start border-2 ps-3">
                              {item.text}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-12">
            <div className="card border">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h6 className="mb-0">Q11 • Bonus/Malus proposti</h6>
                  <span className="badge bg-secondary">{stats.open.q11.length}</span>
                </div>
                {stats.open.q11.length === 0 ? (
                  <div className="text-muted small text-center py-3">Nessuna risposta</div>
                ) : (
                  <div className="accordion accordion-flush" id="accordionQ11">
                    {stats.open.q11.map((item, index) => (
                      <div className="accordion-item border-0" key={index}>
                        <h2 className="accordion-header">
                          <button
                            className="accordion-button collapsed py-2"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target={`#collapseQ11-${index}`}
                          >
                            <span className="me-2 text-muted">#{index + 1}</span>
                            <span className="text-truncate">{item.meta.split('•')[0]?.trim() || 'Utente'}</span>
                          </button>
                        </h2>
                        <div
                          id={`collapseQ11-${index}`}
                          className="accordion-collapse collapse"
                          data-bs-parent="#accordionQ11"
                        >
                          <div className="accordion-body pt-0">
                            <div className="text-muted small mb-2">{item.meta}</div>
                            <div className="border-start border-2 ps-3">
                              {item.text}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-12">
            <div className="card border">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h6 className="mb-0">Feedback libero</h6>
                  <span className="badge bg-secondary">{stats.open.feedback.length}</span>
                </div>
                {stats.open.feedback.length === 0 ? (
                  <div className="text-muted small text-center py-3">Nessuna risposta</div>
                ) : (
                  <div className="accordion accordion-flush" id="accordionFeedback">
                    {stats.open.feedback.map((item, index) => (
                      <div className="accordion-item border-0" key={index}>
                        <h2 className="accordion-header">
                          <button
                            className="accordion-button collapsed py-2"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target={`#collapseFeedback-${index}`}
                          >
                            <span className="me-2 text-muted">#{index + 1}</span>
                            <span className="text-truncate">{item.meta.split('•')[0]?.trim() || 'Utente'}</span>
                          </button>
                        </h2>
                        <div
                          id={`collapseFeedback-${index}`}
                          className="accordion-collapse collapse"
                          data-bs-parent="#accordionFeedback"
                        >
                          <div className="accordion-body pt-0">
                            <div className="text-muted small mb-2">{item.meta}</div>
                            <div className="border-start border-2 ps-3">
                              {item.text}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ELENCO RISPOSTE - VERSIONE MINIMAL */}
      <div className="mb-5">
        <h5 className="mb-3 pb-2 border-bottom">Elenco risposte</h5>
        <div className="card border">
          <div className="card-body">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div className="small text-muted">
                Mostrando {openAllMerged.length} di {stats.total} risposte
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-sm align-middle">
                <thead>
                  <tr className="text-muted border-bottom">
                    <th>Data</th>
                    <th>Utente</th>
                    <th className="text-center">Road To</th>
                    <th className="text-center">Social</th>
                    <th className="text-center">Merch</th>
                    <th className="text-end">Aperte</th>
                  </tr>
                </thead>
                <tbody>
                  {openAllMerged.map((r) => {
                    const full = normalizedMap.get(r.id);
                    return (
                      <tr key={r.id} className={r.hasOpen ? '' : 'opacity-50'}>
                        <td className="small">
                          {r.createdAt ? r.createdAt.toLocaleDateString("it-IT") : '-'}
                        </td>
                        <td className="small text-truncate" style={{ maxWidth: '150px' }}>
                          {r.email || 'Anonimo'}
                        </td>
                        <td className="text-center">
                          <span className={`badge ${full?.q12_roadTo === "Sì" ? 'bg-success' : 'bg-light text-dark'}`}>
                            {full?.q12_roadTo || '-'}
                          </span>
                        </td>
                        <td className="text-center">
                          <span className={`badge ${full?.q13_socialFollow === "Sì" ? 'bg-info' : 'bg-light text-dark'}`}>
                            {full?.q13_socialFollow || '-'}
                          </span>
                        </td>
                        <td className="text-center">
                          <span className={`badge ${full?.q14_merch === "Sì" ? 'bg-warning' : 'bg-light text-dark'}`}>
                            {full?.q14_merch || '-'}
                          </span>
                        </td>
                        <td className="text-end">
                          {r.hasOpen ? (
                            <span className="badge text-bg-success">Sì</span>
                          ) : (
                            <span className="badge text-bg-secondary">No</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {openAllMerged.length === 0 && (
              <div className="text-center py-4">
                <div className="text-muted">Nessuna risposta trovata</div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="text-center text-muted small mt-4 pt-3 border-top">
        Dashboard aggiornata automaticamente
      </div>
    </div>
  );
}
